import React, { useState, useEffect } from 'react';
import { Plus, List, Settings, LogOut, User, Star, Globe, Sparkles } from 'lucide-react';
import ShoppingListCard from './ShoppingListCard';
import ShoppingListView from './ShoppingListView';
import CreateListModal from './CreateListModal';
import ShareListModal from './ShareListModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import SettingsPage from './SettingsPage';
import { apiService } from '../services/apiService';
import realtimeService from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const ShoppingListManager = ({ user, onLogout, onReady }) => {
  console.log('🔄 ShoppingListManager: Component rendered with user:', user?.email);
  
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareList, setShareList] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferList, setTransferList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialListLoaded, setInitialListLoaded] = useState(false);
  const [statsRefreshTrigger, setStatsRefreshTrigger] = useState(0);
  const [specificListRefresh, setSpecificListRefresh] = useState({});
  const [showSettings, setShowSettings] = useState(() => {
    return localStorage.getItem('listWizShowSettings') === 'true';
  });
  const [isFullyReady, setIsFullyReady] = useState(() => {
    // Check if we were previously ready to avoid re-triggering the loading overlay
    return localStorage.getItem('listWizManagerReady') === 'true';
  });
  const { setDefaultList } = useAuth();
  const { t } = useLanguage();

  console.log('🔄 ShoppingListManager: Component state:', {
    loading,
    listsLength: lists.length,
    selectedList: selectedList?.id,
    initialListLoaded,
    isFullyReady
  });

  useEffect(() => {
    console.log('🔄 ShoppingListManager: useEffect for loadLists called');
    loadLists();
  }, []);

  // Clear stored list when user logs out
  useEffect(() => {
    if (!user) {
      localStorage.removeItem('listWizLastViewedList');
      localStorage.removeItem('listWizManagerReady');
      console.log('User logged out, cleared stored list and ready state');
      setIsFullyReady(false);
    }
  }, [user]);

  // Persist settings page state to localStorage
  useEffect(() => {
    localStorage.setItem('listWizShowSettings', showSettings.toString());
  }, [showSettings]);

  // Listen for list blocking events
  useEffect(() => {
    const handleListBlocked = (data) => {
      const { listId, isBlocked, listName } = data;
      console.log(`🚫 List ${isBlocked ? 'blocked' : 'unblocked'}: ${listName} (ID: ${listId})`);
      
      if (isBlocked) {
        // If the currently selected list is blocked, go back to list manager
        if (selectedList && selectedList.id === parseInt(listId)) {
          setSelectedList(null);
          localStorage.removeItem('listWizLastViewedList');
        }
        
        // Remove the blocked list from the lists array immediately
        setLists(prevLists => prevLists.filter(list => list.id !== parseInt(listId)));
      } else {
        // If a list is unblocked, refresh the lists to show it again
        loadLists();
      }
    };

    const handleItemChanged = (data) => {
      console.log('🔄 Item changed on list:', data.listId, 'refreshing specific list stats');
      // Only refresh the specific list that changed
      setSpecificListRefresh(prev => ({
        ...prev,
        [data.listId]: (prev[data.listId] || 0) + 1
      }));
    };

    const handleListChanged = (data) => {
      console.log('🔄 List changed:', data.listId, 'refreshing specific list');
      // Only refresh the specific list that changed
      setSpecificListRefresh(prev => ({
        ...prev,
        [data.listId]: (prev[data.listId] || 0) + 1
      }));
    };

    const handleListStructureChanged = (data) => {
      console.log('🔄 List structure changed (created/shared/unshared):', data, 'reloading all lists');
      // When lists are created, shared, or unshared, we need to reload the full list
      loadLists();
    };

    const handleNeedRefresh = () => {
      console.log('🔄 Background refresh requested, reloading lists');
      // For background refresh, refresh all lists
      setStatsRefreshTrigger(prev => prev + 1);
      loadLists();
    };

    realtimeService.connect();
    realtimeService.on('list-blocked', handleListBlocked);
    realtimeService.on('item-added', handleItemChanged);
    realtimeService.on('item-updated', handleItemChanged);
    realtimeService.on('item-deleted', handleItemChanged);
    realtimeService.on('list-cleared', handleListChanged);
    realtimeService.on('list-unchecked', handleListChanged);
    realtimeService.on('list-created', handleListStructureChanged);
    realtimeService.on('list-shared', handleListStructureChanged);
    realtimeService.on('list-unshared', handleListStructureChanged);
    realtimeService.on('need-refresh', handleNeedRefresh);

    return () => {
      realtimeService.off('list-blocked', handleListBlocked);
      realtimeService.off('item-added', handleItemChanged);
      realtimeService.off('item-updated', handleItemChanged);
      realtimeService.off('item-deleted', handleItemChanged);
      realtimeService.off('list-cleared', handleListChanged);
      realtimeService.off('list-unchecked', handleListChanged);
      realtimeService.off('list-created', handleListStructureChanged);
      realtimeService.off('list-shared', handleListStructureChanged);
      realtimeService.off('list-unshared', handleListStructureChanged);
      realtimeService.off('need-refresh', handleNeedRefresh);
    };
  }, [selectedList]);

  // Auto-open initial list (last viewed or default) when lists first load
  useEffect(() => {
    console.log('🔄 List initialization effect:', {
      listsLength: lists.length,
      selectedList: selectedList?.id,
      initialListLoaded,
      isFullyReady
    });

    if (!initialListLoaded && lists.length > 0) {
      const lastViewedListId = localStorage.getItem('listWizLastViewedList');
      let targetList = null;
      
      if (lastViewedListId) {
        targetList = lists.find(list => list.id === parseInt(lastViewedListId));
        console.log('🔄 Attempting to restore last viewed list:', lastViewedListId, 'Found:', !!targetList);
      }
      
      if (!targetList && user?.defaultListId) {
        targetList = lists.find(list => list.id === user.defaultListId);
        console.log('🔄 Using default list:', user.defaultListId, 'Found:', !!targetList);
      }
      
      if (targetList) {
        selectList(targetList);
      }
      
      setInitialListLoaded(true);
      console.log('🔄 Initial list loaded, notifying parent ready');
    }
  }, [lists, user?.defaultListId, initialListLoaded]);

  // Separate effect to handle app ready state
  useEffect(() => {
    if (!isFullyReady && !loading && lists.length >= 0 && initialListLoaded) {
      console.log('🔄 App is ready, notifying parent');
      setIsFullyReady(true);
      onReady?.();
    }
  }, [loading, lists.length, initialListLoaded, isFullyReady, onReady]);

  const loadLists = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading lists...');
      const data = await apiService.getLists();
      setLists(data);
      console.log('🔄 Lists loaded:', data.length);
      
      if (data.length === 0) {
        setInitialListLoaded(true);
        console.log('🔄 No lists found, marking initial load complete');
      }
    } catch (err) {
      setError('Failed to load shopping lists');
      console.error('Error loading lists:', err);
      setInitialListLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const selectList = (list) => {
    if (list) {
      setSelectedList(list);
      localStorage.setItem('listWizLastViewedList', list.id.toString());
      console.log('Selected list:', list.name, 'ID:', list.id);
    } else {
      setSelectedList(null);
      localStorage.removeItem('listWizLastViewedList');
      console.log('Cleared selected list');
    }
  };

  const handleCreateList = async (listData) => {
    try {
      const newList = await apiService.createList(listData);
      setLists(prev => [...prev, newList]);
      setShowCreateModal(false);
      selectList(newList);
    } catch (err) {
      setError('Failed to create shopping list');
      console.error('Error creating list:', err);
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await apiService.deleteList(listId);
      setLists(prev => prev.filter(list => list.id !== listId));
      if (selectedList && selectedList.id === listId) {
        selectList(null);
      }
    } catch (err) {
      setError('Failed to delete shopping list');
      console.error('Error deleting list:', err);
    }
  };

  const handleUpdateList = async (listId, updates) => {
    try {
      const updatedList = await apiService.updateList(listId, updates);
      setLists(prev => prev.map(list => 
        list.id === listId ? updatedList : list
      ));
      if (selectedList && selectedList.id === listId) {
        setSelectedList(updatedList);
      }
    } catch (err) {
      setError('Failed to update shopping list');
      console.error('Error updating list:', err);
    }
  };

  const handleShareList = async (list) => {
    try {
      const detailedList = await apiService.getList(list.id);
      setShareList(detailedList);
      setShowShareModal(true);
    } catch (err) {
      console.error('Error fetching list details for sharing:', err);
      setShareList(list);
      setShowShareModal(true);
    }
  };

  const handleTransferOwnership = async (list) => {
    try {
      const detailedList = await apiService.getList(list.id);
      setTransferList(detailedList);
      setShowTransferModal(true);
    } catch (err) {
      console.error('Error fetching list details for transfer:', err);
      setTransferList(list);
      setShowTransferModal(true);
    }
  };

  const handleOwnershipTransferred = async (listId, newOwner) => {
    try {
      // Refresh the lists to reflect the ownership change
      await loadLists();
      // If the transferred list was selected, deselect it or update it
      if (selectedList && selectedList.id === listId) {
        setSelectedList(null);
      }
    } catch (err) {
      console.error('Error refreshing lists after ownership transfer:', err);
    }
  };

  const handleUpdateSharedList = async () => {
    try {
      const updatedList = await apiService.getList(shareList.id);
      setLists(prev => prev.map(list => 
        list.id === shareList.id ? updatedList : list
      ));
      setShareList(updatedList);
      if (selectedList && selectedList.id === shareList.id) {
        setSelectedList(updatedList);
      }
    } catch (err) {
      console.error('Error refreshing list:', err);
    }
  };

  if (showSettings) {
    return <SettingsPage user={user} onBack={() => {
      setShowSettings(false);
      localStorage.removeItem('listWizShowSettings');
    }} />;
  }

  // Show clean loading screen until app is fully ready
  if (!isFullyReady) {
    return null; // Let App.js handle the loading screen
  }

  if (selectedList) {
    return (
      <ShoppingListView
        key={selectedList.id}
        list={selectedList}
        onBack={() => {
          selectList(null);
          loadLists();
          setStatsRefreshTrigger(prev => prev + 1);
        }}
        onUpdateList={(updates) => handleUpdateList(selectedList.id, updates)}
        onDeleteList={() => handleDeleteList(selectedList.id)}
      />
    );
  }

  return (
    <div className="shopping-list-manager">
      <header className="app-header">
        <div className="header-content">
          <div className="header-branding">
            <div className="header-title">
              <img src="/favicon-96x96.png" alt="ListWiz Logo" className="header-icon" style={{width: '48px', height: '48px'}} />
              <div className="title-text">
                <h1>
                  ListWiz
                  <span className="version-number" style={{ 
                    fontSize: '10px', 
                    fontWeight: 'normal', 
                    color: '#666', 
                    marginLeft: '8px',
                    opacity: 0.7
                  }}>
                    v0.1.0
                  </span>
                </h1>
                <p className="app-slogan">{t('appSlogan', 'Smart Lists Made Simple')}</p>
              </div>
            </div>
          </div>
          
          <div className="header-actions">
            <button 
              onClick={() => setShowCreateModal(true)}
              className="create-list-btn"
            >
              <Plus size={20} />
              <span className="btn-text">{t('newList', 'New List')}</span>
            </button>

            <div className="user-menu">
              <div 
                className="profile-avatar" 
                title={`${user.name} (${user.email}) - Click to view profile & settings`}
                onClick={() => setShowSettings(true)}
              >
                <User size={20} />
              </div>
              <button 
                onClick={onLogout}
                className="logout-btn"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        {error && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {lists.length === 0 ? (
          <div className="empty-state">
            <div style={{marginBottom: '1.5rem'}}>
              <List size={72} className="empty-icon" color="#667eea" />
            </div>
            <h2>{t('noListsYet', 'No lists yet!')}</h2>
            <p>{t('createFirstList', 'Start by creating your first shopping list.')}</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="create-first-list-btn"
            >
              <Plus size={22} />
              <span>{t('createList', 'Create List')}</span>
            </button>
          </div>
        ) : (
          <div className="lists-grid">
            {lists.map(list => {
              const listRefreshTrigger = specificListRefresh[list.id] || 0;
              return (
                <ShoppingListCard
                  key={list.id}
                  list={list}
                  onClick={() => selectList(list)}
                  onDelete={() => handleDeleteList(list.id)}
                  onEdit={(updates) => handleUpdateList(list.id, updates)}
                  onShare={() => handleShareList(list)}
                  onSetDefault={() => setDefaultList(list.id)}
                  onTransferOwnership={() => handleTransferOwnership(list)}
                  isDefault={user?.defaultListId === list.id}
                  isOwner={!list.isShared}
                  statsRefreshTrigger={statsRefreshTrigger + listRefreshTrigger}
                />
              );
            })}
          </div>
        )}
      </main>

      {showCreateModal && (
        <CreateListModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateList}
        />
      )}

      {showShareModal && shareList && (
        <ShareListModal
          list={shareList}
          onClose={() => {
            setShowShareModal(false);
            setShareList(null);
          }}
          onUpdate={handleUpdateSharedList}
        />
      )}

      {showTransferModal && transferList && (
        <TransferOwnershipModal
          isOpen={showTransferModal}
          list={transferList}
          onClose={() => {
            setShowTransferModal(false);
            setTransferList(null);
          }}
          onTransfer={handleOwnershipTransferred}
        />
      )}
    </div>
  );
};

export default ShoppingListManager;
